import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs';
import {
  ExpenseAction,
  ExpenseActionName,
} from '../models/expense-actions.model';
import { ExpenseNavGroup } from '../models/company-permission.model';
import { ContextService } from './context.service';
import {
  getExpenseListTabs,
  sortExpenseListTabsForDesign,
  sortExpenseNavSubMenusForDesign,
} from '../utils/expense-list-tab.util';

/**
 * UI usage map for BE Expense actionPermissions keys.
 */
export const EXPENSE_ACTION_USAGE: Record<ExpenseActionName, readonly string[]> =
  {
    [ExpenseAction.Create]: [
      'list.createNew',
      'form.createRequest',
      'route.new',
      'sidebar.create',
    ],
    [ExpenseAction.View]: [
      'list.viewRequest',
      'route.list',
      'route.view',
      'sidebar.expenseManagement',
    ],
    [ExpenseAction.Submit]: [
      'form.submit',
      'form.updateRequest',
      'list.editRequest.pending',
      'route.edit.pending',
    ],
    [ExpenseAction.Comment]: ['detail.comment'],
    [ExpenseAction.UpdateDraft]: [
      'form.saveDraft',
      'list.editRequest.draftOrReturned',
      'route.edit.draft',
      'list.deleteDraft',
    ],
    [ExpenseAction.Withdraw]: ['list.withdraw'],
    [ExpenseAction.EditApprovedOrPaymentPending]: [
      'list.editRequest.approvedOrPaymentPending',
      'route.edit.approved',
    ],
    [ExpenseAction.DeleteDraft]: ['list.deleteDraft'],
    [ExpenseAction.Return]: ['detail.return', 'list.return'],
    [ExpenseAction.Reject]: ['detail.reject', 'list.reject'],
    [ExpenseAction.Approve]: ['detail.approve', 'list.approve'],
    [ExpenseAction.Update]: ['form.updateRequest', 'list.editRequest'],
    [ExpenseAction.IntegrateToAP]: ['detail.integrateToAp'],
    [ExpenseAction.MakePayment]: ['detail.makePayment'],
  };

type ExpensePermissionSnapshot = {
  roleId: string | null;
  companyId: string | null;
  actions: ReadonlyMap<string, boolean>;
  navGroup: ExpenseNavGroup | null;
};

/**
 * Central facade for Expense page/action permissions.
 * Resolve rules stay in ContextService; this service caches lookups for UI + guards.
 * Does not affect Accounts Payable or other modules.
 */
@Injectable({
  providedIn: 'root',
})
export class ExpensePermissionService {
  private readonly snapshot$ = new BehaviorSubject<ExpensePermissionSnapshot>(
    this.buildSnapshot()
  );

  readonly actions$ = this.snapshot$.pipe(map((s) => s.actions));
  readonly navGroup$ = this.snapshot$.pipe(map((s) => s.navGroup));

  constructor(private readonly contextSvc: ContextService) {
    combineLatest([
      this.contextSvc.selectedRoleId$.pipe(
        startWith(this.contextSvc.selectedRoleId$.value)
      ),
      this.contextSvc.selectedCompanyId$.pipe(
        startWith(this.contextSvc.getCompanyId())
      ),
      this.contextSvc.user.pipe(startWith(this.contextSvc.user.value)),
      this.contextSvc.onApRolePermissionsChanged$.pipe(startWith(null)),
    ])
      .pipe(
        map(() => this.buildSnapshot()),
        distinctUntilChanged(
          (a, b) =>
            a.roleId === b.roleId &&
            a.companyId === b.companyId &&
            this.sameActionMap(a.actions, b.actions) &&
            JSON.stringify(a.navGroup) === JSON.stringify(b.navGroup)
        )
      )
      .subscribe((snapshot) => this.snapshot$.next(snapshot));
  }

  can(action: ExpenseActionName | string): boolean {
    const snapshot = this.snapshot$.value;
    if (snapshot.actions.has(action)) {
      return snapshot.actions.get(action) === true;
    }

    return this.contextSvc.isExpenseActionAllowed(action);
  }

  canAny(actions: ReadonlyArray<ExpenseActionName | string>): boolean {
    return actions.some((action) => this.can(action));
  }

  canAll(actions: ReadonlyArray<ExpenseActionName | string>): boolean {
    return actions.every((action) => this.can(action));
  }

  getNavGroup(): ExpenseNavGroup | null {
    const group = this.snapshot$.value.navGroup;
    if (!group?.subMenus?.length) {
      return group;
    }
    return {
      ...group,
      subMenus: sortExpenseNavSubMenusForDesign(group.subMenus),
    };
  }

  getExpenseListTabs() {
    return sortExpenseListTabsForDesign(
      getExpenseListTabs(
        this.contextSvc.getStoredPermissionsForRole(
          this.snapshot$.value.roleId
        ),
        this.snapshot$.value.companyId
      )
    );
  }

  hasSidebar(): boolean {
    return Boolean(this.getNavGroup()?.subMenus?.length);
  }

  canCreate(): boolean {
    return this.can(ExpenseAction.Create);
  }

  canView(): boolean {
    return this.can(ExpenseAction.View) || this.hasSidebar();
  }

  canEditRoute(): boolean {
    return this.canAny([
      ExpenseAction.UpdateDraft,
      ExpenseAction.Submit,
      ExpenseAction.Update,
      ExpenseAction.EditApprovedOrPaymentPending,
    ]);
  }

  canEditForStatus(status: string | null | undefined): boolean {
    const normalized = (status ?? '').toLowerCase();

    if (!status || normalized === 'draft' || normalized === 'returned') {
      return this.can(ExpenseAction.UpdateDraft);
    }

    if (
      normalized === 'pendingapproval' ||
      normalized === 'pending approval'
    ) {
      return this.canAny([ExpenseAction.Submit, ExpenseAction.Update]);
    }

    if (
      normalized === 'approved' ||
      normalized === 'paymentpending' ||
      normalized === 'payment pending'
    ) {
      return this.can(ExpenseAction.EditApprovedOrPaymentPending);
    }

    return this.canEditRoute();
  }

  refresh(): void {
    this.snapshot$.next(this.buildSnapshot());
  }

  private buildSnapshot(): ExpensePermissionSnapshot {
    const roleId =
      this.contextSvc.selectedRoleId$.value ??
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('selectedRoleId')
        : null);

    const actions = new Map<string, boolean>();
    (Object.values(ExpenseAction) as string[]).forEach((action) => {
      actions.set(action, this.contextSvc.isExpenseActionAllowed(action));
    });

    return {
      roleId,
      companyId: this.contextSvc.getCompanyId(),
      actions,
      navGroup: this.contextSvc.getExpenseNavGroup(),
    };
  }

  private sameActionMap(
    a: ReadonlyMap<string, boolean>,
    b: ReadonlyMap<string, boolean>
  ): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const [key, value] of a) {
      if (b.get(key) !== value) {
        return false;
      }
    }
    return true;
  }
}
